import { Shadow, ErrorBoundary, Button, HStack, VStack } from '@genial/components';
import { Typography, BoxRadius } from '@genial/design-tokens';
import { Box, FlatList, ScrollView, View } from 'native-base';
import React from 'react';

import { OffersInfoDataProps, ProductsDataProps } from '../../utils/types';

import { OfferSummary } from './components/OfferSummary';
import { OfferSummaryHeader } from './components/OfferSummaryHeader';
import { ProductAccordion } from './components/ProductAccordion';
import { ProductTable } from './components/ProductTable';
import { SkeletonItem } from './components/SkeletonItem';

interface CustomerOffersProps {
  retryBatchTicketInvestLink: () => void;
  handleConfirmOrder: () => void;
  handleRefuseOrder: () => void;
  handleToggleOrderStatus: (id: string) => void;
  listBatchTicketLink: ProductsDataProps[];
  errorBatchTicketLink: Error | undefined;
  loadingBatchTicketLink: boolean;
  offersInfoData: OffersInfoDataProps;
}

export const ContainerMobile: React.FC<CustomerOffersProps> = ({
  retryBatchTicketInvestLink,
  handleConfirmOrder,
  handleRefuseOrder,
  handleToggleOrderStatus,
  listBatchTicketLink,
  errorBatchTicketLink,
  loadingBatchTicketLink,
  offersInfoData,
}) => {
  if (loadingBatchTicketLink) {
    return <SkeletonItem />;
  }

  if (errorBatchTicketLink) {
    return <ErrorBoundary onRefresh={retryBatchTicketInvestLink} {...errorBatchTicketLink} />;
  }

  if (!loadingBatchTicketLink && !errorBatchTicketLink && listBatchTicketLink?.length === 0) {
    return (
      <Box py={{ sm: 'inline.md' }}>
        <ErrorBoundary
          onRefresh={retryBatchTicketInvestLink}
          errorIconOrIllustration="NotEmptyIllustration"
          errorTitle="Nenhuma oferta encontrada."
          errorMessage="No momento não há nenhuma oferta disponível."
        />
      </Box>
    );
  }

  const groupedProductData = listBatchTicketLink.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {} as Record<string, ProductsDataProps[]>);

  const typeOrder = Object.keys(groupedProductData);

  return (
    <View minW="container">
      <ScrollView flex={1} showsVerticalScrollIndicator={false}>
        <VStack px="inline.xxxs" py="stack.xxs" space="inline.xxs">
          <VStack p="inline.xxxs" borderRadius="sm" space="stack.xxs">
            <OfferSummaryHeader
              totalValue={offersInfoData.totalCustomerOffersAmount}
              orderHash={offersInfoData.basketAdvisorOrderHash}
            />
            <FlatList
              showsVerticalScrollIndicator={false}
              data={offersInfoData.headerOrderData}
              ItemSeparatorComponent={() => (
                <Box h="0.5px" my="stack.nano" w="full" bg="neutral.lightest" />
              )}
              keyExtractor={(item) => item.label}
              renderItem={({ item }) => <OfferSummary item={item} />}
            />
          </VStack>

          {typeOrder?.map((type, index) => (
            <VStack key={type} bg={'neutral.white'} borderRadius={BoxRadius.sm} p="inline.xxxs">
              <ProductAccordion
                title={type}
                initiallyOpen={index === 0}
                content={groupedProductData[type].map((product) => (
                  <ProductTable
                    key={product.id}
                    productData={product}
                    handleToggleOrderStatus={handleToggleOrderStatus}
                  />
                ))}
              />
            </VStack>
          ))}
        </VStack>
      </ScrollView>
      <Shadow py="inline.xxs" px="inline.xxxs" bg="neutral.white" level={1}>
        <HStack justifyContent="space-between">
          <Button
            variant="secondary"
            onPress={handleRefuseOrder}
            p="inline.nano"
            w="48%"
            minH="inline.sm"
            _text={{
              fontFamily: Typography['fontfamily.base.semibold'],
            }}>
            Recusar
          </Button>
          <Button
            onPress={handleConfirmOrder}
            p="inline.nano"
            w="48%"
            minH="inline.sm"
            _text={{
              fontFamily: Typography['fontfamily.base.semibold'],
            }}>
            Confirmar
          </Button>
        </HStack>
      </Shadow>
    </View>
  );
};
